import { memo, useMemo } from "react";

import { useCreateLoanLutTransaction } from "@bridgesplit/abf-react";
import { Column, TagTextAlert } from "@bridgesplit/ui";
import { AppButton } from "app/components/common";
import { useTransactionSender } from "app/components/transactions";

import { RaydiumContextType } from "../raydium/types";
import { WhirlpoolContextType } from "../whirlpool/types";
import { ActionType } from "./ManagePositionWrapper";
import { getActionType, getActionTypeInitalized } from "./util";

export default memo(function ManagePositionCta({
    context,
    actionType
}: {
    context: RaydiumContextType;
    actionType: ActionType;
}) {
    const createLoanLut = useCreateLoanLut(context, actionType);
    const { isActionTypeInitialized } = context;

    const ctaError = useMemo(() => {
        if (isActionTypeInitialized === undefined) return "Loading";
        if (getActionTypeInitalized(actionType, isActionTypeInitialized)) return "LUTs already initialized";
        return undefined;
    }, [isActionTypeInitialized, actionType]);

    const ctaWarning = useMemo(() => {
        if (isActionTypeInitialized === undefined) return undefined;
        if (!getActionTypeInitalized(actionType, isActionTypeInitialized)) {
            if (actionType === "withdraw" || actionType === "deposit") {
                return "You need to sign a one-time transaction to enable position deposits and withdrawals";
            } else if (actionType === "open") {
                return "You need to sign a one-time transaction to enable position re-initialization";
            }
        }
        return undefined;
    }, [actionType, isActionTypeInitialized]);
    return (
        <Column spacing={1}>
            {!!ctaWarning && (
                <TagTextAlert color="info" icon="help">
                    {ctaWarning}
                </TagTextAlert>
            )}
            <AppButton
                asyncCta={{ onClickWithResult: createLoanLut }}
                isTransaction
                color="secondary"
                disabled={!!ctaError}
            >
                {ctaError ?? "Enable Position Managment"}
            </AppButton>
        </Column>
    );
});

function useCreateLoanLut(context: RaydiumContextType | WhirlpoolContextType, actionType: ActionType) {
    const createLoanLut = useCreateLoanLutTransaction();
    const send = useTransactionSender();

    const { loanExpanded, reset } = context;
    const actionTypeNumber = getActionType(actionType);

    return async () => {
        return await send(
            createLoanLut,
            { loan: loanExpanded.loan.address, actionType: actionTypeNumber },
            {
                description: "Enabling position management",
                sendOptions: {
                    onSuccess: reset
                }
            }
        );
    };
}
